import { useCallback, useEffect, useRef, useState } from 'react';
import { api } from './apiClient';

/* ============================================================================
 * Single source-of-truth fetch for the Operations dashboard.
 *
 * Mirrors App.tsx applyClientSideFilters (lines 170-365) which is the
 * authoritative formula used by the legacy ExecutiveDashboard. The dashboard
 * needs SIX independent data sources to compute the real KPIs:
 *
 *   1) /warehouse                       — physical bin rows (rawWarehouseData)
 *   2) /warehouse/virtual               — virtual storage   (rawVirtualData)
 *   3) /warehouse/customer-storage-merged — customer pallets (incl. MANUAL & FLOOR)
 *   4) /warehouse/area-customer-blocked       — LGPGM039 blocked bin list
 *   5) /warehouse/area-customer-blocked/stats — header counts (BIN_BLOCKED_COUNT)
 *   6) /customer-contracts?active=true        — committed-pallet contracts
 *
 * The hook returns the raw arrays AS-IS plus a thin "rows" projection of the
 * physical bins for the existing v2 row-level filtering / drill code.
 *
 * AUTO-REFRESH: Every 5 minutes the hook re-fetches all data from the server.
 * A pause flag lets the user suspend/resume auto-refresh. Manual refresh is
 * also exposed via `refresh()`.
 * ========================================================================== */

const REFRESH_INTERVAL_MS = 5 * 60 * 1000; // 5 minutes

export type WarehouseRow = {
  WAREHOUSE: string;
  STORAGE_AREA: string;
  SHELF_SIZE: string;
  BIN_LOCATION: string;
  TOTAL_BINS: number;        // capacity
  USED_BINS: number;         // used (full cap when temp blocked)
  FREE_BINS: number;
  TEMP_BLOCKED_BINS: number;
  OCCUPIED_BINS: number;
  PARTIAL_BINS: number;
  BLOCKED: boolean;
  SEG: number;
  FREE_SEG: number;
  LVL: number;
  // Raw passthroughs needed by the multi-source aggregator
  SOURCE: string;
  LPFSTA: string;
  OCCUPANCY_STATUS: string;
};

const num = (v: any): number => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

const safeGet = (path: string) =>
  api.get(path).then(r => r.data).catch(() => null);

export type WarehouseDataset = {
  rows: WarehouseRow[];          // physical bins (filtered: not MANUAL, seg>0 && seg<99)
  rawAll: any[];                 // ALL raw bins from /warehouse incl. virtual/manual
  virtual: any[];                // /warehouse/virtual
  customerStorage: any[];        // /warehouse/customer-storage-merged
  areaBlocked: any[];            // /warehouse/area-customer-blocked
  areaBlockedStats: any;         // /warehouse/area-customer-blocked/stats
  contracts: any[];              // /customer-contracts?active=true
  weeklyEntries: any[];          // /weekly-entries — raw manual capacity
};

const EMPTY: WarehouseDataset = {
  rows: [], rawAll: [], virtual: [], customerStorage: [],
  areaBlocked: [], areaBlockedStats: {}, contracts: [], weeklyEntries: [],
};

export function useWarehouseRows() {
  const [data, setData] = useState<WarehouseDataset>(EMPTY);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [paused, setPaused] = useState(false);
  const [lastRefresh, setLastRefresh] = useState<Date | null>(null);

  // Track whether the component is still mounted
  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  // Core fetch function — reusable for initial load + refresh
  const fetchAll = useCallback(async (isInitial = false) => {
    if (isInitial) setLoading(true);
    try {
      const [wh, virt, cust, blocked, blockedStats, contracts, weekly] = await Promise.all([
        safeGet('/warehouse'),
        safeGet('/warehouse/virtual'),
        safeGet('/warehouse/customer-storage-merged'),
        safeGet('/warehouse/area-customer-blocked'),
        safeGet('/warehouse/area-customer-blocked/stats'),
        safeGet('/customer-contracts?active=true'),
        safeGet(`/weekly-entries?year=${new Date().getFullYear()}`),
      ]);

      if (!mountedRef.current) return;

      const unwrap = (r: any): any[] => {
        if (!r) return [];
        if (Array.isArray(r)) return r;
        if (Array.isArray(r.data)) return r.data;
        if (Array.isArray(r.warehouses)) return r.warehouses;
        return [];
      };
      const unwrapStats = (r: any): any => {
        if (!r) return {};
        return r.data?.data ?? r.data ?? r ?? {};
      };

      const rawAll       = unwrap(wh);
      const virtualArr   = unwrap(virt);
      const customerArr  = unwrap(cust);
      const blockedArr   = unwrap(blocked);
      const blockedStatsObj = unwrapStats(blockedStats);
      const contractsArr = unwrap(contracts);
      const weeklyArr    = unwrap(weekly);

      // Build the per-bin "rows" projection used by drill + row-filter code.
      // Match legacy filter: drop MANUAL synthetic, virtual (seg=99), floor (seg=0).
      const rows: WarehouseRow[] = rawAll
        .filter((b: any) => {
          if (b?.SOURCE === 'MANUAL') return false;
          const seg = num(b?.TOTAL_NO_OF_SEGMENTS);
          if (seg >= 99) return false;
          if (seg === 0) return false;
          return true;
        })
        .map((b: any) => {
          const seg  = num(b.TOTAL_NO_OF_SEGMENTS);
          const free = num(b.FREE_SEGMENTS);
          const lvl  = num(b.NUMBER_OF_LEVELS);
          const blockedFlag =
            String(b.LPFSTA || '').trim() === '2' ||
            b.OCCUPANCY_STATUS === 'TEMP_BLOCKED';
          const cap     = seg;
          const used    = blockedFlag ? cap : Math.max(0, seg - free);
          const freePal = blockedFlag ? 0   : free;
          return {
            WAREHOUSE:    String(b.WAREHOUSE ?? '').trim(),
            STORAGE_AREA: String(b.STORAGE_AREA ?? '').trim() || 'OTHER',
            SHELF_SIZE:   String(b.SHELF_SIZE ?? '').trim() || 'Unknown',
            BIN_LOCATION: String(
              b.BIN_LOCATION
                ?? (b.LPREG != null && b.LPHOR != null && b.LPVER != null
                    ? `${String(b.LPREG).trim()}-${String(b.LPHOR).padStart(3, '0')}-${String(b.LPVER).trim()}`
                    : '')
            ).trim() || '—',
            TOTAL_BINS:        cap,
            USED_BINS:         used,
            FREE_BINS:         freePal,
            TEMP_BLOCKED_BINS: blockedFlag ? cap : 0,
            OCCUPIED_BINS:     used,
            PARTIAL_BINS:      0,
            BLOCKED:           blockedFlag,
            SEG: seg, FREE_SEG: free, LVL: lvl,
            SOURCE:           String(b.SOURCE ?? '').trim(),
            LPFSTA:           String(b.LPFSTA ?? '').trim(),
            OCCUPANCY_STATUS: String(b.OCCUPANCY_STATUS ?? '').trim(),
          };
        })
        .filter(r => r.WAREHOUSE);

      // Normalize rawAll fields to match WarehouseRow conventions.
      // Without this, bins with empty SHELF_SIZE are excluded by filterRaw's
      // pallet-type filter (they become '') even though the processed `rows`
      // have 'Unknown'. This mismatch causes whStatsBase > sum(whStats across
      // all pallet types), making individual pallet type totals undercount.
      const rawAllNorm = rawAll.map((b: any) => ({
        ...b,
        SHELF_SIZE:   String(b.SHELF_SIZE ?? '').trim() || 'Unknown',
        STORAGE_AREA: String(b.STORAGE_AREA ?? '').trim() || 'OTHER',
      }));

      setData({
        rows,
        rawAll: rawAllNorm,
        virtual: virtualArr,
        customerStorage: customerArr,
        areaBlocked: blockedArr,
        areaBlockedStats: blockedStatsObj,
        contracts: contractsArr,
        weeklyEntries: weeklyArr,
      });
      setError(rawAll.length ? null : 'No warehouse data returned');
      setLastRefresh(new Date());
    } catch (e: any) {
      if (!mountedRef.current) return;
      setError(e?.message ?? 'fetch failed');
    } finally {
      if (mountedRef.current) setLoading(false);
    }
  }, []);

  // Initial fetch
  useEffect(() => {
    fetchAll(true);
  }, [fetchAll]);

  // Auto-refresh interval (pauses when `paused` is true)
  useEffect(() => {
    if (paused) return;
    const id = setInterval(() => { fetchAll(false); }, REFRESH_INTERVAL_MS);
    return () => clearInterval(id);
  }, [paused, fetchAll]);

  return {
    ...data,
    loading,
    error,
    // Auto-refresh controls
    paused,
    setPaused,
    lastRefresh,
    refresh: () => fetchAll(false),
  };
}
